import { LanguageCode, ServiceDepartmentListApiResponse } from '@navio/nsdk';
import { URLKeys } from 'noddi-async';
import { type FilterOptionSource, usePagination } from 'noddi-ui-web';
import { useDebounceValue } from 'noddi-util';
import { toFilterOptionSource, useFilterOptionsGet } from '../useFilterOptionsGet';

/** Title, description and visible options of this filter, for the builder next to its options. */
export const SERVICE_DEPARTMENT_FILTER_TEXT = {
  title: 'Service departments',
  description: 'Filter by specific service departments or locations'
};

/** Service departments as filter options, searchable. For `unit.multiSelect`. */
export const useServiceDepartmentOptions = ({
  serviceOrganizationIds,
  languageCodes,
  brandIds,
  hasEnabledAdapter,
  enabled = true
}: {
  serviceOrganizationIds?: number[];
  languageCodes?: LanguageCode[];
  brandIds?: number[];
  /** Only departments whose service organization has an adapter enabled for the current env. */
  hasEnabledAdapter?: boolean;
  enabled?: boolean;
} = {}): FilterOptionSource<number> => {
  const { pageIndex, pageSize, search, setSearch } = usePagination();
  const debouncedSearch = useDebounceValue({ value: search, delay: 300 });

  const query = useFilterOptionsGet({
    type: URLKeys.getPaginatedServiceDepartments,
    input: {
      pageIndex,
      pageSize,
      search: debouncedSearch,
      serviceOrganizationIds,
      brandIds,
      language: languageCodes,
      hasEnabledAdapter
    },
    queryConfig: { enabled, select: toServiceDepartmentOptions }
  });

  return toFilterOptionSource(query, 'number', { search, onSearch: setSearch });
};

const toServiceDepartmentOptions = ({ results }: ServiceDepartmentListApiResponse) =>
  results.map((department) => ({
    value: department.id,
    label: department.userGroup.name,
    imageUrl: department.profileImage?.url ?? department.brand.logo?.url
  }));
